import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '@common/components/ConfirmDialog';
import { useDeleteConfirmation } from '@common/hooks/useDeleteConfirmation';
import { ShareModal, type ShareResourceType } from '@features/shares';
import { useDownload } from './useDownload';
import { useBulkDownload } from './useBulkDownload';
import { useDeleteFile } from './useDeleteFile';
import { useBulkDelete } from './useBulkDelete';
import { useDeleteFolder } from '@features/folders';
import { triggerBrowserDownload } from '@common/utils/download';
import { fileService } from '../services/fileService';
import type { File } from '../types';
import type { Folder } from '@features/folders';

interface ShareTarget {
  resourceType: ShareResourceType;
  resourceId: string;
  resourceName: string;
}

interface BulkDeleteRequest {
  folders: Folder[];
  files: File[];
}

/**
 * Centraliza ações sobre arquivos/pastas (play, download, delete com
 * confirmação, share modal) — individuais e em lote — usadas no FileExplorer.
 *
 * O consumidor renderiza `dialogs` em algum lugar do JSX e usa os `handlers`.
 */
export function useFileActions() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { mutate: download } = useDownload();
  const { mutate: bulkDownload } = useBulkDownload();
  const { mutate: deleteFile } = useDeleteFile();
  const { mutate: deleteFolder } = useDeleteFolder();
  const deleteConfirmation = useDeleteConfirmation();
  const { deleteMany: bulkDelete } = useBulkDelete();
  const [shareTarget, setShareTarget] = useState<ShareTarget | null>(null);
  const [bulkDeletePending, setBulkDeletePending] = useState<BulkDeleteRequest | null>(null);

  const bulkCount = bulkDeletePending
    ? bulkDeletePending.folders.length + bulkDeletePending.files.length
    : 0;

  const handlers = {
    handlePlayFile: (file: File) => navigate(`/files/view/${file.id}`),
    handleDownloadFile: (file: File) => download(file.id),
    handleDeleteFile: (file: File) =>
      deleteConfirmation.requestDelete(file.name, () => deleteFile(file.id)),
    handleDeleteFolder: (folder: Folder) =>
      deleteConfirmation.requestDelete(folder.name, () => deleteFolder(folder.id)),
    handleShareFile: (file: File) =>
      setShareTarget({
        resourceType: 'file',
        resourceId: file.id,
        resourceName: file.name,
      }),
    handleShareFolder: (folder: Folder) =>
      setShareTarget({
        resourceType: 'folder',
        resourceId: folder.id,
        resourceName: folder.name,
      }),
    handleDownloadFolder: (folder: Folder) => {
      triggerBrowserDownload(fileService.getFolderDownloadUrl(folder.id));
    },
    /**
     * Recebe arquivos E pastas selecionados na seleção múltipla. Estratégia:
     *  - se houver pastas, baixa cada pasta como ZIP individual (endpoint
     *    /folders/:id/download) + um único ZIP para os arquivos soltos;
     *  - sem pastas, faz um único bulk-download dos arquivos.
     */
    handleBulkDownload: (payload: { files: File[]; folders?: Folder[] }) => {
      const { files, folders = [] } = payload;
      for (const folder of folders) {
        triggerBrowserDownload(fileService.getFolderDownloadUrl(folder.id));
      }
      if (files.length > 0) {
        bulkDownload(files.map((f) => f.id));
      }
    },
    handleBulkDelete: (payload: BulkDeleteRequest) => {
      if (payload.folders.length + payload.files.length === 0) return;
      setBulkDeletePending(payload);
    },
  };

  const dialogs = (
    <>
      {shareTarget && (
        <ShareModal
          isOpen
          onClose={() => setShareTarget(null)}
          resourceType={shareTarget.resourceType}
          resourceId={shareTarget.resourceId}
          resourceName={shareTarget.resourceName}
        />
      )}

      <ConfirmDialog
        open={deleteConfirmation.isOpen}
        title={t('confirmDelete.title')}
        description={t('confirmDelete.description', {
          name: deleteConfirmation.pending?.name ?? '',
        })}
        confirmLabel={t('confirmDelete.confirm')}
        onConfirm={deleteConfirmation.confirm}
        onCancel={deleteConfirmation.cancel}
      />

      <ConfirmDialog
        open={!!bulkDeletePending}
        title={t('bulkSelection.confirmDeleteTitle')}
        description={t('bulkSelection.confirmDeleteDescription', { count: bulkCount })}
        confirmLabel={t('confirmDelete.confirm')}
        onConfirm={async () => {
          if (!bulkDeletePending) return;
          const pending = bulkDeletePending;
          setBulkDeletePending(null);
          await bulkDelete(pending);
        }}
        onCancel={() => setBulkDeletePending(null)}
      />
    </>
  );

  return { handlers, dialogs };
}
